/**
 * Promise-based confirm/prompt dialogs — the app's replacement for
 * `window.confirm()` / `window.prompt()`.
 *
 * Native JS dialogs are banned here for two reasons. They break the design
 * system (browser-chrome styling, no theming, no `data-mol-id`). And they park
 * the renderer's main thread in an untimed synchronous browser wait — the exact
 * stack signature of a recurring permanent tab wedge (2026-08-27: main thread
 * captured twice in an infinite stack-allocated condvar wait; Chromium also
 * auto-dismisses native dialogs on tab switch, a known-fragile resume path).
 * These dialogs are ordinary React state — nothing blocks.
 *
 * Usage: mount `<AppDialogsHost />` once (App.tsx), then from any handler:
 *
 *   if (!(await confirmDialog({ message, danger: true }))) return
 *   const name = await promptDialog({ message, defaultValue })  // null = cancelled
 *
 * Requests queue FIFO if one arrives while another is open.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Button, Flex } from '@molecule/app-ui-react'

import { useUiProviders } from '../hooks/useUiProviders.js'

/** Options for {@link confirmDialog}. */
export interface ConfirmDialogOptions {
  /** Body text — the question being asked. */
  message: string
  /** Optional short title above the message. */
  title?: string
  /** Confirm button label (default: localized "OK"). */
  confirmLabel?: string
  /** Cancel button label (default: localized "Cancel"). */
  cancelLabel?: string
  /** Style the confirm button as destructive (error color). */
  danger?: boolean
  /** `data-mol-id` for the dialog (default `app-dialog`). */
  molId?: string
}

/** Options for {@link promptDialog}. */
export interface PromptDialogOptions {
  /** Body text — what the value is for. */
  message: string
  /** Optional short title above the message. */
  title?: string
  /** Initial input value. */
  defaultValue?: string
  /** Input placeholder. */
  placeholder?: string
  /** Confirm button label (default: localized "OK"). */
  confirmLabel?: string
  /** Cancel button label (default: localized "Cancel"). */
  cancelLabel?: string
  /** `data-mol-id` for the dialog (default `app-dialog`). */
  molId?: string
}

type DialogRequest =
  | { kind: 'confirm'; options: ConfirmDialogOptions; resolve: (ok: boolean) => void }
  | { kind: 'prompt'; options: PromptDialogOptions; resolve: (value: string | null) => void }

/** FIFO of pending requests; index 0 is the open dialog. */
const queue: DialogRequest[] = []
/** The mounted host's re-render trigger (null when no host is mounted). */
let notifyHost: (() => void) | null = null

const enqueue = (request: DialogRequest): void => {
  queue.push(request)
  notifyHost?.()
}

/**
 * Ask the user to confirm an action. Resolves `true` on confirm, `false` on
 * cancel/Escape/backdrop — never blocks the main thread.
 * @param options - Message, labels, and styling.
 * @returns Whether the user confirmed.
 */
export const confirmDialog = (options: ConfirmDialogOptions): Promise<boolean> =>
  new Promise((resolve) => enqueue({ kind: 'confirm', options, resolve }))

/**
 * Ask the user for a line of text. Resolves the entered string on confirm, or
 * `null` on cancel/Escape/backdrop (matching `window.prompt` semantics).
 * @param options - Message, default value, labels.
 * @returns The entered value, or null when cancelled.
 */
export const promptDialog = (options: PromptDialogOptions): Promise<string | null> =>
  new Promise((resolve) => enqueue({ kind: 'prompt', options, resolve }))

/**
 * Renders the open dialog (if any). Mount exactly once, above the router so
 * every page shares it.
 * @returns The dialog overlay, or null when idle.
 */
export function AppDialogsHost() {
  const { t } = useTranslation()
  const [, setTick] = useState(0)
  const current: DialogRequest | undefined = queue[0]
  const [value, setValue] = useState('')
  const focusRef = useRef<HTMLInputElement | HTMLButtonElement | null>(null)
  // The SAFE button of a destructive confirm: a stray Enter must cancel, not delete.
  const cancelRef = useRef<HTMLButtonElement | null>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  // What had focus when the dialog opened, so closing it puts the user back where they were.
  const openerRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    notifyHost = () => setTick((n) => n + 1)
    return () => {
      notifyHost = null
    }
  }, [])

  // Seed the prompt input from the request's default when a dialog opens.
  const requestRef = useRef<DialogRequest | undefined>(undefined)
  if (requestRef.current !== current) {
    requestRef.current = current
    // Safe during render: state for the dialog that is about to paint.
    setValue(current?.kind === 'prompt' ? (current.options.defaultValue ?? '') : '')
  }

  useEffect(() => {
    if (current) {
      if (!openerRef.current && document.activeElement instanceof HTMLElement) {
        openerRef.current = document.activeElement
      }
      const destructive = current.kind === 'confirm' && current.options.danger === true
      ;(destructive ? cancelRef.current : focusRef.current)?.focus()
    } else if (openerRef.current) {
      const opener = openerRef.current
      openerRef.current = null
      // The opener may have been removed (a deleted card): focus() on a detached node is a no-op.
      if (opener.isConnected) opener.focus()
    }
  }, [current])

  const settle = useCallback(
    (result: boolean | string | null) => {
      const request = queue.shift()
      if (!request) return
      if (request.kind === 'confirm') request.resolve(result === true)
      else request.resolve(typeof result === 'string' ? result : null)
      setTick((n) => n + 1)
    },
    [setTick],
  )

  // Escape cancels the open dialog.
  useEffect(() => {
    if (!current) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        settle(null)
        return
      }
      // Keep Tab inside the dialog: it is aria-modal, so the page behind must not be reachable.
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
          ),
        )
        if (focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        const active = document.activeElement
        if (e.shiftKey && (active === first || !dialogRef.current.contains(active))) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && (active === last || !dialogRef.current.contains(active))) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [current, settle])

  // The host mounts above the router on EVERY route, and the landing route
  // never wires the ClassMap bonds — an un-gated getClassMap() would throw and
  // take out the whole page (see eager-classmap-gate.test.ts). Gate the render
  // until the UI bonds are wired, loading them on demand when a dialog opens.
  const uiReady = useUiProviders(current !== undefined)

  if (!current || !uiReady) return null

  const cm = getClassMap()
  const { options } = current
  const molId = options.molId ?? 'app-dialog'
  const confirmLabel = options.confirmLabel ?? t('common.ok', undefined, { defaultValue: 'OK' })
  const cancelLabel =
    options.cancelLabel ?? t('common.cancel', undefined, { defaultValue: 'Cancel' })
  const danger = current.kind === 'confirm' && current.options.danger

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'rgba(0, 0, 0, 0.45)',
        backdropFilter: 'blur(4px)',
      }}
      data-mol-id={`${molId}-backdrop`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) settle(null)
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={options.title ?? options.message}
        className={cm.cn(cm.surface, cm.sp('p', 5))}
        style={{
          borderRadius: 12,
          width: 'min(420px, 92vw)',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
        data-mol-id={molId}
      >
        {options.title && (
          <div className={cm.cn(cm.textSize('base'), cm.fontWeight('semibold'))}>
            {options.title}
          </div>
        )}
        <div className={cm.textSize('sm')} style={{ whiteSpace: 'pre-wrap' }}>
          {options.message}
        </div>
        {current.kind === 'prompt' && (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              settle(value)
            }}
          >
            <input
              ref={(el) => {
                focusRef.current = el
              }}
              type="text"
              className={cm.input()}
              value={value}
              placeholder={current.options.placeholder}
              onChange={(e) => setValue(e.target.value)}
              onFocus={(e) => e.target.select()}
              data-mol-id={`${molId}-input`}
            />
          </form>
        )}
        <Flex justify="end" align="center" gap={2}>
          <Button
            ref={(el: HTMLButtonElement | null) => {
              cancelRef.current = el
            }}
            variant="ghost"
            size="sm"
            onClick={() => settle(null)}
            data-mol-id={`${molId}-cancel`}
          >
            {cancelLabel}
          </Button>
          <Button
            ref={(el: HTMLButtonElement | null) => {
              if (current.kind === 'confirm') focusRef.current = el
            }}
            variant="solid"
            color={danger ? 'error' : 'primary'}
            size="sm"
            onClick={() => settle(current.kind === 'prompt' ? value : true)}
            data-mol-id={`${molId}-confirm`}
          >
            {confirmLabel}
          </Button>
        </Flex>
      </div>
    </div>
  )
}
